/**
 * ============================================================================
 * The section's shelf — files and links for the room (M26)
 * ============================================================================
 * Brian: the course build should hold "the files or related session info …
 * so it is local when an instructor needs it." One component, two seats:
 *
 *   manage   — on the section page: add a link, upload a file, take one down
 *   (default) — on Run it and Present: the list, and one tap to open
 *
 * OPENING IS A ROUND TRIP ON PURPOSE. Files live in a private bucket; the
 * backend hands out a one-hour signed URL only after RLS has agreed the
 * caller may see the section. The window is opened BEFORE the fetch so the
 * browser treats it as the user's click and does not block the pop-up.
 *
 * Uploads are sent as raw bytes with the name and type in headers — one file
 * per request, no multipart library on either side.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../lib/api.js';
import { supabase } from '../lib/supabase.js';
import ErrorBanner from './ErrorBanner.jsx';
import Field from './Field.jsx';

const baseUrl = (import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8080').replace(/\/+$/, '');
const MAX_FILE_BYTES = 50 * 1024 * 1024;

function sizeLabel(bytes) {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function kindLabel(m) {
  if (m.kind === 'link') return 'Link';
  const t = m.content_type ?? '';
  if (t.includes('pdf')) return 'PDF';
  if (t.includes('presentation') || t.includes('powerpoint')) return 'Slides';
  if (t.startsWith('video/')) return 'Video';
  if (t.startsWith('image/')) return 'Image';
  if (t.includes('word')) return 'Document';
  return 'File';
}

export function MaterialsShelf({ classId, manage = false, compact = false }) {
  const [materials, setMaterials] = useState(undefined);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState({ title: '', url: '' });
  const [addingLink, setAddingLink] = useState(false);
  const fileInput = useRef(null);

  const load = useCallback(async () => {
    try {
      const data = await apiFetch(`/api/v1/classes/${classId}/materials`);
      setMaterials(data.materials ?? []);
    } catch (err) {
      setError(err);
      setMaterials(null);
    }
  }, [classId]);

  useEffect(() => {
    if (classId) load();
  }, [classId, load]);

  async function open(m) {
    // Open first, fill in after: a window opened inside the click is the
    // user's; one opened after an await is a pop-up to be blocked.
    const w = window.open('', '_blank', 'noopener');
    try {
      const data = await apiFetch(`/api/v1/materials/${m.id}/open`);
      if (w) w.location = data.url;
      else window.location.assign(data.url);
    } catch (err) {
      if (w) w.close();
      setError(err);
    }
  }

  async function addLink(event) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await apiFetch(`/api/v1/classes/${classId}/materials/links`, {
        method: 'POST',
        body: JSON.stringify({ title: link.title.trim(), url: link.url.trim() }),
      });
      setLink({ title: '', url: '' });
      setAddingLink(false);
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function upload(event) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setError(null);
    if (file.size > MAX_FILE_BYTES) {
      setError({ message: `${file.name} is ${sizeLabel(file.size)}; the limit is 50 MB. A video that size is better as a link.` });
      return;
    }
    setBusy(true);
    try {
      const { data } = await supabase.auth.getSession();
      const token = data?.session?.access_token;
      const res = await fetch(`${baseUrl}/api/v1/classes/${classId}/materials/files`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/octet-stream',
          'x-file-name': encodeURIComponent(file.name),
          'x-file-type': file.type || 'application/octet-stream',
          'x-file-title': encodeURIComponent(file.name.replace(/\.[^.]+$/, '')),
        },
        body: file,
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(body?.error?.message ?? `Upload failed (${res.status}).`);
      }
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function remove(m) {
    if (!window.confirm(`Take down “${m.title}”? Instructors will no longer see it on this section.`)) return;
    setError(null);
    setBusy(true);
    try {
      await apiFetch(`/api/v1/classes/${classId}/materials/${m.id}`, { method: 'DELETE' });
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={compact ? 'shelf shelf--compact' : 'shelf'}>
      <ErrorBanner error={error} />

      {materials === undefined && (
        <p className="muted" aria-busy="true">
          Loading…
        </p>
      )}

      {materials && materials.length === 0 && (
        <p className="muted">
          {manage
            ? 'Nothing on the shelf yet. Add the slides, a video link, the handout — whatever the instructor should have to hand.'
            : 'Nothing on this section’s shelf.'}
        </p>
      )}

      {materials && materials.length > 0 && (
        <ul className="shelf__list">
          {materials.map((m) => (
            <li key={m.id} className="shelf__item">
              <button type="button" className="shelf__open" onClick={() => open(m)} disabled={busy}>
                <span className="badge shelf__kind">{kindLabel(m)}</span>
                <span className="shelf__title">{m.title}</span>
                {m.kind === 'file' && m.size_bytes ? <span className="muted shelf__meta">{sizeLabel(m.size_bytes)}</span> : null}
                {m.kind === 'link' && <span className="muted shelf__meta">{new URL(m.url).hostname}</span>}
              </button>
              {manage && (
                <button type="button" className="btn btn--quiet" onClick={() => remove(m)} disabled={busy}>
                  Take down
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {manage && (
        <div className="shelf__actions">
          <button
            type="button"
            className="btn btn--auto"
            onClick={() => fileInput.current?.click()}
            disabled={busy}
          >
            {busy ? 'Working…' : 'Upload a file'}
          </button>
          <input ref={fileInput} type="file" hidden onChange={upload} />
          {!addingLink && (
            <button type="button" className="btn btn--quiet btn--auto" onClick={() => setAddingLink(true)} disabled={busy}>
              Add a link
            </button>
          )}
          <span className="muted shelf__hint">Up to 50 MB a file. Bigger videos: add the link instead.</span>
        </div>
      )}

      {manage && addingLink && (
        <form className="shelf__linkform" onSubmit={addLink}>
          <Field label="What is it?" value={link.title} onChange={(v) => setLink((l) => ({ ...l, title: v }))} disabled={busy} />
          <Field
            label="Address"
            type="url"
            value={link.url}
            onChange={(v) => setLink((l) => ({ ...l, url: v }))}
            hint="https://…"
            disabled={busy}
          />
          <div className="shelf__actions">
            <button type="submit" className="btn btn--primary btn--auto" disabled={busy || !link.title.trim() || !link.url.trim()}>
              Add link
            </button>
            <button type="button" className="btn btn--quiet btn--auto" onClick={() => setAddingLink(false)} disabled={busy}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export default MaterialsShelf;
